import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkDeviceSignalTag } from '~/api/device-signal-tags/bulk';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { assert } from '~/lib/assert';

export interface BulkTagDialogProps {
  signalIds: string[];
  signalTags: string[];
  onClose: () => void;
  onComplete: () => void;
  open: boolean;
  tags: string[];
}

export const BulkTagDialog = ({
  signalIds,
  signalTags,
  onClose,
  onComplete,
  open,
  tags,
}: BulkTagDialogProps) => {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const [bulkTag, { called }] = useBulkDeviceSignalTag();

  const handleClose = useCallback(() => {
    setSelectedTag(() => null);
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>
        {t('pages.device_signals.list.bulk_tag_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Autocomplete
            freeSolo
            fullWidth
            onChange={(_event, option) => setSelectedTag(option ?? null)}
            onInputChange={(_, value) => setSelectedTag(value)}
            options={signalTags}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('common.tags.choose_tag')}
                variant="outlined"
              />
            )}
            value={selectedTag || null}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={called || !selectedTag}
          onClick={async () => {
            assert(selectedTag != null, 'tag is null');
            await bulkTag({
              variables: {
                items: signalIds.map((x) => {
                  return {
                    taggableId: Number(x),
                    taggableType: 'DEVICE_SIGNAL',
                  };
                }),
                tag: selectedTag,
                tags,
              },
            });
            handleClose();
            onComplete();
          }}
        >
          {t('common.dialogs.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
